import { Category } from '@wix/answers-api/dist/kb';
import { ConsumerSource, DeviceType, InnerLinkComponent, SearchResultItem } from '@wix/cc-user-facing-common-components';
import {
  Box,
  Divider,
  Heading,
  Text,
} from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, useContext } from 'react';

import {
  EXPERIMENTS,
} from '../../../constants';
import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import { ArticleSearchResult, ArticleSearchResultItem } from '../../../types';
import { isMobile } from '../../../utils/user-agent';
import { useStore } from '../../../zustand/stores';
import { InnerLink } from '../../InnerLink';
import { useSearchResultsBi } from '../bi';
import css from '../index.module.scss';


type SearchResultItemsProps = {
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  term: string;
};

export const SearchResultItems: FC<SearchResultItemsProps> = ({
  categoriesPaths,
  searchResults,
  term,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const context = useContext(Context);
  const searchSessionId = useStore((state) => state.biSlice.searchSessionId);
  const { pageType } = context;
  const { experiments } = useExperiments({ readOnly: true });
  const biEvents = useSearchResultsBi(term, locale as string, searchSessionId);
  const isMobileView = isMobile(context);

  const items = searchResults.items;

  const onArticleTopicBadgeClick = (topic: Category, itemOrder: number) => {
    biEvents.onArticleTopicBadgeClick(topic, itemOrder);
  };

  const onArticleClick = (article: ArticleSearchResultItem, itemOrder: number) => {
    biEvents.onArticleClick(article, itemOrder, pageType, searchResults.searchMethod);
  };

  const getSearchResultsItems = () => items.map((searchResult, idx) => {
    const rootTopic = searchResult.categoryId && categoriesPaths?.[searchResult.categoryId]?.[0];
    const itemOrder = idx + 1;
    return (
      <>
        {experiments.enabled(EXPERIMENTS.SPECS.SHOW_DISTANCE_FOR_RAM) && (
          <Text weight="bold">Distance: {searchResult.distance}</Text>
        )}
        <SearchResultItem
          consumerSource={ConsumerSource.HELP_CENTER}
          locale={locale as string}
          key={searchResult.id}
          deviceType={isMobileView ? DeviceType.MOBILE : DeviceType.DESKTOP}
          innerLinkComponent={InnerLink as InnerLinkComponent}
          rootTopic={rootTopic || undefined}
          onArticleTopicBadgeClick={(topic) => onArticleTopicBadgeClick(topic, itemOrder)}
          onArticleClick={() => onArticleClick(searchResult, itemOrder)}
          searchResultItem={searchResult}
          linkComponentFn={(props) => (
            <Link
              href={(props.href || props.to) as string}
              onClick={props.onClick}
              className={props.className}
              target={props.target}
              data-hook={props.dataHook}
            >
              {props.children}
            </Link>
          )}
        />
        {idx < items.length - 1 ? <Divider /> : null}
      </>
    );
  });

  const getEmptySearchResults = () => (
    <Box
      direction="vertical"
      align="center"
      className={css.noResultsForFilter}
      dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOR_FILTER}
    >
      <Heading appearance="H3" className={css.noResultsForFilterTitle}>
        {t('search-results-page.no-results-found-under-filter.title', { term })}
      </Heading>
      <Text size="medium" className={css.noResultsForFilterDescription}>
        {t('search-results-page.no-results-found-under-filter.description')}
      </Text>
    </Box>
  );


  return (
    <Box direction="vertical" className={css.searchResultsList}>
      {items.length ? getSearchResultsItems() : getEmptySearchResults()}
    </Box>
  );
};
